import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Filter, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { RootState } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { analytics } from '@/services/analytics';
import {
  fetchAdminExchangeRates,
  refreshAdminExchangeRates,
  type AdminExchangeRate,
} from '@/services/adminService';
import { logging } from '@/services/logging';
import { useToast } from '@/hooks/use-toast';

const LIMIT_OPTIONS = [50, 100, 200, 300, 500] as const;

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const AdminExchangeRatesPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const { toast } = useToast();

  const [rates, setRates] = useState<ReadonlyArray<AdminExchangeRate>>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [baseCurrency, setBaseCurrency] = useState('');
  const [quoteCurrency, setQuoteCurrency] = useState('');
  const [source, setSource] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState<number>(200);

  const hasFilters = useMemo(
    () => Boolean(baseCurrency || quoteCurrency || source || startDate || endDate || limit !== 200),
    [baseCurrency, quoteCurrency, source, startDate, endDate, limit],
  );

  const loadRates = useCallback(async () => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchAdminExchangeRates({
        baseCurrency: baseCurrency || undefined,
        quoteCurrency: quoteCurrency || undefined,
        source: source || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        limit,
      });
      setRates(result);
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to load admin exchange rates', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to load exchange rates.';
      setError(message);
      setStatus('error');
      toast({ title: 'Failed to load exchange rates', description: message, variant: 'destructive' });
    }
  }, [baseCurrency, quoteCurrency, source, startDate, endDate, limit, toast]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void loadRates();
  }, [isAdmin, loadRates, navigate]);

  const handleRefresh = async () => {
    analytics.track('button: ui.admin_exchange_rates_refresh');
    try {
      setRefreshing(true);
      const summary = await refreshAdminExchangeRates();
      toast({
        title: 'Exchange rates updated',
        description: summary.effectiveDate
          ? `Fetched ${summary.persistedPairs}/${summary.processedPairs} pair(s) · Effective ${formatDateTime(summary.effectiveDate)}`
          : `Processed ${summary.processedPairs} pair(s)` ,
      });
      await loadRates();
    } catch (err) {
      logging.error('Failed to refresh admin exchange rates', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to refresh exchange rates.';
      toast({ title: 'Refresh failed', description: message, variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  const handleApplyFilters = async () => {
    analytics.track('button: ui.admin_exchange_rates_apply_filters', {
      baseCurrency: baseCurrency || null,
      quoteCurrency: quoteCurrency || null,
      source: source || null,
      startDate: startDate || null,
      endDate: endDate || null,
      limit,
    });
    await loadRates();
  };

  const handleResetFilters = () => {
    analytics.track('button: ui.admin_exchange_rates_reset_filters');
    setBaseCurrency('');
    setQuoteCurrency('');
    setSource('');
    setStartDate('');
    setEndDate('');
    setLimit(200);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-8">
      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-2xl">Exchange rate control</CardTitle>
              <CardDescription>Refresh National Bank of Ukraine rates and review stored history.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { analytics.track('button: ui.admin_exchange_rates_reload'); void loadRates(); }} disabled={status === 'loading'} variant="outline">
                {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Reload table
              </Button>
              <Button onClick={() => void handleRefresh()} disabled={refreshing}>
                {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Refresh today&apos;s rates
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Filter className="h-5 w-5" /> Filter exchange rates
            </CardTitle>
            <CardDescription>Restrict the table using currency codes, source, or effective date range.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="baseCurrency">Base currency</Label>
                <Input
                  id="baseCurrency"
                  value={baseCurrency}
                  onChange={(event) => setBaseCurrency(event.target.value.toUpperCase())}
                  placeholder="e.g. USD"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quoteCurrency">Quote currency</Label>
                <Input
                  id="quoteCurrency"
                  value={quoteCurrency}
                  onChange={(event) => setQuoteCurrency(event.target.value.toUpperCase())}
                  placeholder="e.g. UAH"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="source">Source</Label>
                <Input
                  id="source"
                  value={source}
                  onChange={(event) => setSource(event.target.value)}
                  placeholder="e.g. NBU"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="startDate">Start date</Label>
                <div className="relative">
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                  <CalendarClock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End date</Label>
                <div className="relative">
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                  <CalendarClock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="limit">Max rows</Label>
                <Select value={String(limit)} onValueChange={(value) => setLimit(Number(value))}>
                  <SelectTrigger id="limit">
                    <SelectValue placeholder="200" />
                  </SelectTrigger>
                  <SelectContent>
                    {LIMIT_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void handleApplyFilters()} disabled={status === 'loading'}>
                {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Filter className="mr-2 h-4 w-4" />}
                Apply filters
              </Button>
              <Button variant="outline" onClick={handleResetFilters} disabled={!hasFilters}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Stored exchange rates</CardTitle>
            <CardDescription>
              Showing {rates.length} entr{rates.length === 1 ? 'y' : 'ies'}. Most recent effective date listed first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status === 'loading' && rates.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading exchange rates…
              </div>
            ) : error && rates.length === 0 ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-destructive">
                {error}
              </div>
            ) : rates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/40 p-10 text-center text-muted-foreground">
                No exchange rates match your filters yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Base</TableHead>
                      <TableHead>Quote</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Effective date</TableHead>
                      <TableHead>Fetched at</TableHead>
                      <TableHead>Recorded at</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rates.map((rate) => (
                      <TableRow key={rate.id}>
                        <TableCell className="font-semibold">{rate.baseCurrency}</TableCell>
                        <TableCell>{rate.quoteCurrency}</TableCell>
                        <TableCell>{rate.rate.toFixed(6)}</TableCell>
                        <TableCell>{rate.source}</TableCell>
                        <TableCell>{formatDateTime(rate.effectiveDate)}</TableCell>
                        <TableCell>{formatDateTime(rate.fetchedAt)}</TableCell>
                        <TableCell>{formatDateTime(rate.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminExchangeRatesPage;
